import React, { useState } from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import SEO from "../components/seo";

import "./index.scss";
import DevisForm from "../components/DevisForm";
import Services from "../components/Services";
import Certifications from "../components/Certifications";
import Testimonials from "../components/Testimonials";
import { useImage } from "../components/AsyncImage";
import { sendWcbEvent } from "../functions";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { useScrollLock } from "../hooks/useScrollLock";
import { normalizeUrl } from "../utils/normalizeUrl";

const HeaderImage = ({ src }) => {
  const { hasLoaded, hasError } = useImage(src);

  let imageClass = "";
  let style = {};

  if (hasError) {
    return null;
  }

  if (hasLoaded) {
    imageClass = "loaded";
    style = {
      backgroundImage: `url(${src})`,
    };
  }

  return <div className={"image-wallpaper " + imageClass} style={style} />;
};

const HomepageTemplate = ({ data: { wpPage, file } }) => {
  const [showForm, setShowForm] = useState(false);

  // Utiliser le hook pour bloquer le scroll quand le formulaire est ouvert
  useScrollLock(showForm);

  const featuredImage = {
    fluid: wpPage.featuredImage?.node?.localFile?.childImageSharp?.fluid,
    publicURL: wpPage.featuredImage?.node?.localFile?.publicURL,
    alt: wpPage.featuredImage?.node?.alt || ``,
  };

  let src = featuredImage?.fluid?.src || featuredImage?.publicURL || "";

  return (
    <Layout isHomePage={wpPage.isFrontPage}>
      <SEO title={wpPage.title} description={wpPage.content} post={wpPage} />
      <div className="homepage">
        <section className="section first-block">
          <HeaderImage src={src} />
          <div className="inner">
            <div
              dangerouslySetInnerHTML={{
                __html: wpPage.acfHomepage?.firstBlock?.text,
              }}
            />
            {/**
            <a
              href="tel:+33695434091"
              className="call-to-action toTel"
              onClick={() => sendWcbEvent()}
            >
              06 95 43 40 91
            </a>
            <span className="local">*Prix d'un appel local</span>
            <span className="rappel">Rappel gratuit et immédiat</span>
             */}

            <a
              href="#"
              className="call-to-action green"
              onClick={(e) => {
                e.preventDefault();
                setShowForm(true);
              }}
            >
              {wpPage.acfHomepage?.firstBlock?.callToAction?.title}
            </a>
          </div>
        </section>

        <section className="section hero-services-block">
          <div className="inner">
            <div className="hero-services-content">
              <div className="hero-services-text m-padding">
                <div
                  dangerouslySetInnerHTML={{
                    __html: wpPage.acfHomepage?.secondBlock?.text,
                  }}
                />
                <a
                  href={normalizeUrl(
                    wpPage.acfHomepage?.secondBlock?.callToAction?.url
                  )}
                  className="call-to-action phone-number"
                  onClick={() => sendWcbEvent()}
                >
                  <PhoneIcon
                    style={{
                      width: "18px",
                    }}
                  />
                  {wpPage.acfHomepage?.secondBlock?.callToAction?.title}
                </a>
              </div>
              <div className="hero-services-image">
                <img
                  src={
                    wpPage.acfHomepage?.secondBlock?.image?.localFile
                      ?.childImageSharp?.fluid?.src ||
                    wpPage.acfHomepage?.secondBlock?.image?.localFile?.publicURL
                  }
                  alt={wpPage.acfHomepage?.secondBlock?.image?.altText}
                />
              </div>
            </div>
          </div>
        </section>

        <Services
          services={wpPage.acfHomepage?.thirdBlock?.services || []}
          text={wpPage.acfHomepage?.thirdBlock?.text}
        />

        <Certifications homepage={wpPage.acfHomepage} />

        <Testimonials homepage={wpPage.acfHomepage} />

        <section className="section service-area-block m-padding">
          <div className="inner">
            <div className="service-area-content">
              <div className="service-area-text">
                <div
                  dangerouslySetInnerHTML={{
                    __html: wpPage.acfHomepage?.sixthBlock?.text,
                  }}
                />
              </div>
              <div className="service-area-map">
                <img
                  src={
                    wpPage.acfHomepage?.sixthBlock?.map?.localFile
                      ?.childImageSharp?.fixed?.src ||
                    wpPage.acfHomepage?.sixthBlock?.map?.localFile?.publicURL
                  }
                  alt={wpPage.acfHomepage?.sixthBlock?.map?.altText}
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Popup DevisForm */}
      {showForm && (
        <div className="devis-popup-overlay" onClick={() => setShowForm(false)}>
          <div className="devis-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="devis-popup-close"
              onClick={() => setShowForm(false)}
            >
              ×
            </button>
            <DevisForm />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default HomepageTemplate;

export const pageQuery = graphql`
  query getHomepage {
    file(relativePath: { eq: "wallpaper-home.png" }) {
      childImageSharp {
        fluid(maxWidth: 1700, quality: 90, toFormat: JPG) {
          src
        }
      }
    }
    wpPage(isFrontPage: { in: true }) {
      id
      uri
      title
      content
      isFrontPage
      slug
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1700, quality: 90, toFormat: JPG) {
                src
              }
            }
            publicURL
          }
        }
      }
      seo {
        canonical
        title
        metaDesc
        focuskw
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphAuthor
        opengraphDescription
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
        }
        opengraphUrl
        opengraphSiteName
        opengraphPublishedTime
        opengraphModifiedTime
        twitterTitle
        twitterDescription
        twitterImage {
          altText
          sourceUrl
          srcSet
        }
        breadcrumbs {
          url
          text
        }
        cornerstone
        schema {
          pageType
          articleType
          raw
        }
        readingTime
      }
      acfHomepage {
        firstBlock {
          callToAction {
            title
          }
          text
        }
        secondBlock {
          text
          callToAction {
            title
            url
          }
          image {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 400, quality: 90, toFormat: JPG) {
                  src
                }
              }
              publicURL
            }
          }
        }
        thirdBlock {
          text
          services {
            description
            title
            link {
              title
              url
            }
            image {
              altText
              localFile {
                childImageSharp {
                  fluid(maxWidth: 400, quality: 90, toFormat: JPG) {
                    src
                  }
                }
              }
            }
          }
        }
        fourthBlock {
          text
          block1 {
            text
            callToAction {
              title
              url
            }
          }
          block2 {
            text
            callToAction {
              title
              url
            }
          }
          image {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 1062, quality: 90, toFormat: JPG) {
                  src
                }
              }
              publicURL
            }
          }
        }
        sixthBlock {
          text
          map {
            altText
            localFile {
              childImageSharp {
                fixed(width: 165, jpegQuality: 10) {
                  src
                }
              }
              publicURL
            }
          }
        }
        fifthBlock {
          text
          testimonials {
            title
            subtitle
            description
            note
          }
          logos {
            logo {
              altText
              localFile {
                childImageSharp {
                  fluid(maxWidth: 100, quality: 90, toFormat: JPG) {
                    src
                  }
                }
                publicURL
              }
            }
          }
        }
        fieldGroupName
      }
    }
  }
`;
